import React, { useState } from "react";
import { Box, Card, CardContent, Typography, Tooltip } from "@mui/material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PinDropIcon from '@mui/icons-material/PinDrop';
import { Link as RouterLink } from "react-router-dom";
import { APP_ROUTES } from "~/single-page/constants/routes";

export default function ActionSlideCard({ action, hideOngName = false, isPastAction = false }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Card
      component={RouterLink}
      to={`${APP_ROUTES.ACTION_MANAGEMENT}/${action.id}`}
      onClick={() => setIsLoading(true)}
      className="action-card"
      variant="outlined"
      role="article"
      aria-label={`Ação: ${action.title}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        display: "flex",
        backgroundColor: "white",
        width: "100%",
        maxWidth: "100%",
        height: action.ong && !hideOngName ? "180px" : "160px",
        gap: "4px",
        transition: 'all 0.3s ease',
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
        opacity: isLoading ? 0.7 : (isPastAction ? 0.7 : 1),
        position: 'relative',
        overflow: 'hidden',
        ...(isPastAction && {
          filter: 'grayscale(0.9)',
          bgcolor: 'rgba(0, 0, 0, 0.07)',
          borderColor: 'rgba(0, 0, 0, 0.1)',
        }),
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          '& .hover-message': {
            opacity: 1,
          }
        },
        '&::after': action.ong && !hideOngName ? {
          content: '""',
          position: 'absolute',
          bottom: '45px',
          left: 0,
          right: 0,
          height: '1px',
          mb: 0,
          backgroundColor: 'rgba(0,0,0,0.1)',
          zIndex: 10,
        } : {}
      }}
    >
      <Box
        className="hover-message"
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          backgroundColor: 'secondary.main',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          textAlign: 'center',
          opacity: 0,
          transition: 'opacity 0.3s ease',
          fontSize: '11px',
          fontWeight: 500,
          zIndex: 2,
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        }}
      >
        Ver detalhes
      </Box>

      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          minWidth: 0,
          padding: "16px !important",
          paddingTop: "15px !important",
          position: 'relative',
          '&:last-child': {
            paddingBottom: "16px"
          }
        }}
      >
        <Tooltip title={action.title} arrow placement="top">
          <Typography 
            variant="h6" 
            fontWeight="bold" 
            fontSize="12px"
            sx={{ 
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {action.title}
          </Typography>
        </Tooltip>

        <Typography 
          variant="caption" 
          fontSize="10px"
          sx={{
            mb: 1,
            lineHeight: '1.6',
            flex: isHovered ? '1 0 auto' : '0 0 auto',
            maxHeight: isHovered ? 'none' : '30px',
            overflow: 'hidden',
            textAlign: 'justify',
            color: 'text.secondary',
            letterSpacing: '0.01em',
            transition: 'all 0.3s ease',
            minWidth: 0,
          }}
        >
          {action.short_description}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            mt: 'auto',
            flex: '1 0 auto',
            pt: 0.5,
            mb: 1,
            position: 'relative',
            zIndex: 1,
            backgroundColor: isPastAction ? 'transparent' : 'white',
            opacity: isHovered ? 0 : 1,
            height: isHovered ? 0 : 'auto',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            minWidth: 0,
          }}
        >
          {action.start_date && (
            <Typography 
              variant="caption" 
              fontSize="10px"
              sx={{
                display: 'flex',
                alignItems: 'center', 
                gap: 1,
                color: 'text.secondary',
                width: '100%',
                minWidth: 0,
              }}
            >
              <DateRangeIcon 
                color="secondary" 
                fontSize="small"
                sx={{ flexShrink: 0 }}
              />
              <Box 
                component="span"
                sx={{
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                  flex: '1 1 auto',
                }}
              >
                {`${new Date(action.start_date).toLocaleDateString('pt-BR', { 
                  day: '2-digit', 
                  month: '2-digit', 
                  year: 'numeric'
                })}, ${new Date(action.start_date).toLocaleTimeString('pt-BR', { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}`}
              </Box>
            </Typography>
          )}

          {action.location && (
            <Typography 
              variant="caption" 
              fontSize="10px"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'text.secondary',
                width: '100%',
                minWidth: 0,
              }}
            >
              <PinDropIcon 
                color="secondary" 
                fontSize="small"
                sx={{ flexShrink: 0 }}
              />
              <Box 
                component="span"
                sx={{
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                  flex: '1 1 auto',
                }}
              >
                {action.location}
              </Box>
            </Typography>
          )}
        </Box>

        {action.ong && !hideOngName && (
          <Box
            sx={{
              mt: 0,
              pt: 0,
              mb: 0,
              position: 'relative',
              top: '-24px',
              zIndex: 1,
              backgroundColor: 'white',
              flex: '0 0 auto',
              backgroundColor: isPastAction ? 'transparent' : 'white',
              minWidth: 0,
            }}
          >
            <Typography 
              variant="caption" 
              fontSize="12px"
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                color: 'text.primary',
                width: '100%',
                position: 'relative',
                minWidth: 0,
              }}
            >
              <ApartmentIcon 
                color="secondary" 
                fontSize="small"
                sx={{ 
                  flexShrink: 0, 
                  position: 'relative',
                  top: '-3px'
                }}
              />
              <Box 
                component="span"
                sx={{
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: 1.3,
                  fontWeight: 500,
                  wordBreak: 'break-word',
                  mt: '-1px',
                  minWidth: 0,
                  flex: '1 1 auto',
                }}
              >
                {action.ong.name}
              </Box>
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}